/**
 * Chain configuration — ONE object, built from a preset plus env overrides,
 * shared by the server (watcher, hot wallet, SIWE) and the browser bundle
 * (wagmi). Nothing chain-specific is hardcoded anywhere else: flipping the
 * site to Robinhood Chain is `CHAIN_PRESET=robinhoodTestnet` (chain 46630) or
 * `CHAIN_PRESET=robinhood` (mainnet 4663) plus, for production, an
 * authenticated CHAIN_RPC_URL (see README "Flip to Robinhood Chain").
 *
 * Robinhood Chain is an Arbitrum Orbit (Nitro) L2 with Ethereum as parent and
 * ETH as gas. A preset overridden with blank CHAIN_* values still fails loudly
 * at boot (assertChainConfig) instead of pointing at a guessed endpoint.
 */

export interface ChainConfig {
  preset: string;
  id: number;
  name: string;
  rpcUrl: string;
  wsUrl: string | null;
  explorerUrl: string | null;
  nativeCurrency: { name: string; symbol: string; decimals: number };
  /** Block confirmations before a deposit is credited. */
  confirmations: number;
  /** True when the preset is a throwaway dev chain (enables faucets etc.). */
  isLocal: boolean;
  /** True when the chain's value is play money. False means a mistake costs real money. */
  isTestnet: boolean;
}

type EnvLike = Record<string, string | undefined>;

export const CHAIN_PRESETS: Readonly<Record<string, Omit<ChainConfig, 'preset' | 'confirmations'>>> = Object.freeze({
  /** Anvil from docker-compose — deterministic accounts, instant blocks. */
  local: {
    id: 31337,
    name: 'Local Anvil',
    rpcUrl: 'http://127.0.0.1:8545',
    wsUrl: null,
    explorerUrl: null,
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    isLocal: true,
    isTestnet: true,
  },
  /** Public Arbitrum testnet — the site runs against it today with no env edits. */
  arbitrumSepolia: {
    id: 421614,
    name: 'Arbitrum Sepolia',
    rpcUrl: 'https://sepolia-rollup.arbitrum.io/rpc',
    wsUrl: null,
    explorerUrl: 'https://sepolia.arbiscan.io',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    isLocal: false,
    isTestnet: true,
  },
  /** Robinhood Chain TESTNET (Arbitrum Orbit L2, parent Ethereum Sepolia).
   *  Gas is test ETH from https://faucet.testnet.chain.robinhood.com. The
   *  public RPC is rate-limited: set CHAIN_RPC_URL to an Alchemy/QuickNode
   *  endpoint for anything beyond a smoke run. */
  robinhoodTestnet: {
    id: 46630,
    name: 'Robinhood Chain Testnet',
    rpcUrl: 'https://rpc.testnet.chain.robinhood.com',
    wsUrl: null,
    explorerUrl: 'https://explorer.testnet.chain.robinhood.com',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    isLocal: false,
    isTestnet: true,
  },
  /** Robinhood Chain MAINNET (Arbitrum Orbit L2, parent Ethereum L1, gas in
   *  ETH, Blockscout explorer). The public RPC below is rate-limited and not
   *  meant for production — override CHAIN_RPC_URL / CHAIN_WS_URL with an
   *  authenticated provider (Alchemy `robinhood-mainnet`, QuickNode, dRPC). */
  robinhood: {
    id: 4663,
    name: 'Robinhood Chain',
    rpcUrl: 'https://rpc.mainnet.chain.robinhood.com',
    wsUrl: null,
    explorerUrl: 'https://robinhoodchain.blockscout.com',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    isLocal: false,
    isTestnet: false,
  },
});

export const DEFAULT_PRESET = 'arbitrumSepolia';

function num(v: string | undefined, fallback: number): number {
  const n = Number(v);
  return v !== undefined && v !== '' && Number.isFinite(n) ? n : fallback;
}

/** Build the config from an env-like record (process.env on the server, the
 *  injected `window.__RH_CONFIG__.chainEnv` in the browser). */
export function resolveChainConfig(env: EnvLike): ChainConfig {
  const presetName = env.CHAIN_PRESET && CHAIN_PRESETS[env.CHAIN_PRESET] ? env.CHAIN_PRESET : DEFAULT_PRESET;
  const p = CHAIN_PRESETS[presetName];
  const cfg: ChainConfig = {
    preset: presetName,
    id: num(env.CHAIN_ID, p.id),
    name: env.CHAIN_NAME || p.name,
    rpcUrl: env.CHAIN_RPC_URL || p.rpcUrl,
    wsUrl: env.CHAIN_WS_URL || p.wsUrl,
    explorerUrl: env.CHAIN_EXPLORER_URL || p.explorerUrl,
    nativeCurrency: {
      name: env.CHAIN_NATIVE_NAME || p.nativeCurrency.name,
      symbol: env.CHAIN_NATIVE_SYMBOL || p.nativeCurrency.symbol,
      decimals: num(env.CHAIN_NATIVE_DECIMALS, p.nativeCurrency.decimals),
    },
    confirmations: num(env.DEPOSIT_CONFIRMATIONS, 2),
    isLocal: p.isLocal,
    isTestnet: p.isTestnet,
  };
  assertLocalPresetId(cfg);
  assertTestnetPresetId(cfg);
  return cfg;
}

/** isLocal comes from the preset alone and is what turns on the dev faucet
 *  and plain-http RPC in the CSP, so it must never describe a real chain:
 *  `CHAIN_PRESET=local` combined with a CHAIN_ID other than Anvil's 31337 is
 *  refused at config time rather than discovered in production. */
export const LOCAL_CHAIN_ID = 31337;
export function assertLocalPresetId(cfg: ChainConfig): void {
  if (cfg.isLocal && cfg.id !== LOCAL_CHAIN_ID) {
    throw new Error(`chain preset "${cfg.preset}" is the throwaway dev chain (id ${LOCAL_CHAIN_ID}) but CHAIN_ID=${cfg.id} — isLocal enables the dev faucet and must never point at a real chain; unset CHAIN_ID or choose the matching preset`);
  }
}

/** Chain ids that carry real value. A preset claiming to be a testnet while
 *  pointed at one of these is refused: isTestnet decides how harshly the boot
 *  preflight treats a shared treasury key, so it must never be wrong. */
export const REAL_MONEY_CHAIN_IDS = new Set([1, 4663, 42161, 8453, 10, 137]);
export function assertTestnetPresetId(cfg: ChainConfig): void {
  if (cfg.isTestnet && REAL_MONEY_CHAIN_IDS.has(cfg.id)) {
    throw new Error(`chain preset "${cfg.preset}" is a testnet preset but CHAIN_ID=${cfg.id} is a real-money chain — the boot preflight relaxes its rules on a testnet; choose the matching preset instead of overriding CHAIN_ID`);
  }
}

/** Real value at stake: not the dev chain, not a testnet. */
export function isRealMoney(cfg: ChainConfig): boolean {
  return !cfg.isLocal && !cfg.isTestnet;
}

/** Fail loudly on a half-configured chain (the robinhood preset with blanks). */
export function assertChainConfig(cfg: ChainConfig): void {
  assertLocalPresetId(cfg);
  assertTestnetPresetId(cfg);
  const missing: string[] = [];
  if (!cfg.id || cfg.id <= 0) missing.push('CHAIN_ID');
  if (!cfg.rpcUrl) missing.push('CHAIN_RPC_URL');
  if (missing.length) {
    throw new Error(`chain preset "${cfg.preset}" is missing ${missing.join(', ')} — set them in .env (see README "Flip to Robinhood Chain")`);
  }
}

/** viem `Chain` object for the config. */
export function toViemChain(cfg: ChainConfig) {
  return {
    id: cfg.id,
    name: cfg.name,
    nativeCurrency: cfg.nativeCurrency,
    rpcUrls: {
      default: { http: [cfg.rpcUrl], webSocket: cfg.wsUrl ? [cfg.wsUrl] : undefined },
    },
    blockExplorers: cfg.explorerUrl ? { default: { name: 'Explorer', url: cfg.explorerUrl } } : undefined,
  };
}

/** Explorer links (null when the chain has no explorer, e.g. local). */
export function explorerTxUrl(cfg: ChainConfig, hash: string): string | null {
  return cfg.explorerUrl ? `${cfg.explorerUrl.replace(/\/$/, '')}/tx/${hash}` : null;
}
export function explorerAddressUrl(cfg: ChainConfig, addr: string): string | null {
  return cfg.explorerUrl ? `${cfg.explorerUrl.replace(/\/$/, '')}/address/${addr}` : null;
}
